/**
 * Persistent spatial audio emitters for environmental hazards.
 *
 * Each hazard gets a looping PannerNode-based emitter positioned at its
 * world coordinates, routed to the ambient bus. Emitters are created on
 * game start and cleaned up on scene clear.
 *
 * Hazard types:
 * - Volcanic: low rumble + crackle
 * - Geyser: hissing steam noise
 * - Crystal: harmonic hum
 */

import type { HazardState, PlanetState } from '../types';
import { getAudioContext, getAmbientBus } from './sfx';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface HazardEmitter {
  hazardId: number;
  panner: PannerNode;
  output: GainNode;
  cleanup(): void;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function loopingNoise(ctx: AudioContext, duration: number): AudioBufferSourceNode {
  const len = ctx.sampleRate * duration;
  const buf = ctx.createBuffer(1, len, ctx.sampleRate);
  const data = buf.getChannelData(0);
  for (let i = 0; i < len; i++) data[i] = Math.random() * 2 - 1;
  const src = ctx.createBufferSource();
  src.buffer = buf;
  src.loop = true;
  return src;
}

function createPanner(ctx: AudioContext, x: number, y: number, z: number, ref: number, max: number): PannerNode {
  const p = ctx.createPanner();
  p.panningModel = 'equalpower';
  p.distanceModel = 'inverse';
  p.refDistance = ref;
  p.maxDistance = max;
  p.rolloffFactor = 1;
  p.positionX.value = x;
  p.positionY.value = y;
  p.positionZ.value = z;
  return p;
}

// ---------------------------------------------------------------------------
// Emitter builders
// ---------------------------------------------------------------------------

function buildVolcanicEmitter(ctx: AudioContext, panner: PannerNode, out: GainNode): () => void {
  // Low rumble: detuned sub sines
  const o1 = ctx.createOscillator(); o1.type = 'sine'; o1.frequency.value = 40;
  const o2 = ctx.createOscillator(); o2.type = 'sine'; o2.frequency.value = 47;
  const subG = ctx.createGain(); subG.gain.value = 0.15;
  o1.connect(subG); o2.connect(subG);
  subG.connect(out);

  // Crackle: filtered noise with slow modulation
  const noise = loopingNoise(ctx, 2);
  const lp = ctx.createBiquadFilter();
  lp.type = 'lowpass'; lp.frequency.value = 250; lp.Q.value = 1;
  const nGain = ctx.createGain(); nGain.gain.value = 0.08;
  noise.connect(lp).connect(nGain).connect(out);

  const lfo = ctx.createOscillator(); lfo.type = 'sine'; lfo.frequency.value = 0.2;
  const lfoG = ctx.createGain(); lfoG.gain.value = 100;
  lfo.connect(lfoG).connect(lp.frequency);

  o1.start(); o2.start(); noise.start(); lfo.start();

  return () => {
    o1.stop(); o2.stop(); noise.stop(); lfo.stop();
    o1.disconnect(); o2.disconnect(); subG.disconnect();
    noise.disconnect(); lp.disconnect(); nGain.disconnect();
    lfo.disconnect(); lfoG.disconnect();
  };
}

function buildGeyserEmitter(ctx: AudioContext, panner: PannerNode, out: GainNode): () => void {
  // Steam hiss: bandpass noise with amplitude modulation
  const noise = loopingNoise(ctx, 2);
  const bp = ctx.createBiquadFilter();
  bp.type = 'bandpass'; bp.frequency.value = 3000; bp.Q.value = 1.5;
  const nGain = ctx.createGain(); nGain.gain.value = 0.10;
  noise.connect(bp).connect(nGain).connect(out);

  // Amplitude LFO — irregular steam bursts
  const lfo = ctx.createOscillator(); lfo.type = 'sine'; lfo.frequency.value = 0.4;
  const lfoG = ctx.createGain(); lfoG.gain.value = 0.05;
  lfo.connect(lfoG).connect(nGain.gain);

  // Low pressure tone
  const sub = ctx.createOscillator(); sub.type = 'sine'; sub.frequency.value = 80;
  const subG = ctx.createGain(); subG.gain.value = 0.04;
  sub.connect(subG).connect(out);

  noise.start(); lfo.start(); sub.start();

  return () => {
    noise.stop(); lfo.stop(); sub.stop();
    noise.disconnect(); bp.disconnect(); nGain.disconnect();
    lfo.disconnect(); lfoG.disconnect();
    sub.disconnect(); subG.disconnect();
  };
}

function buildCrystalEmitter(ctx: AudioContext, panner: PannerNode, out: GainNode): () => void {
  // Harmonic hum: sine partials with slow amplitude LFOs
  const freqs = [220, 330, 440];
  const nodes: { osc: OscillatorNode; gain: GainNode; lfo: OscillatorNode; lfoG: GainNode }[] = [];

  for (let i = 0; i < freqs.length; i++) {
    const o = ctx.createOscillator(); o.type = 'sine'; o.frequency.value = freqs[i];
    const g = ctx.createGain(); g.gain.value = 0.05 - i * 0.01;
    const l = ctx.createOscillator(); l.type = 'sine'; l.frequency.value = 0.1 + i * 0.06;
    const lg = ctx.createGain(); lg.gain.value = 0.02;
    l.connect(lg).connect(g.gain);
    o.connect(g).connect(out);
    o.start(); l.start();
    nodes.push({ osc: o, gain: g, lfo: l, lfoG: lg });
  }

  return () => {
    for (const n of nodes) {
      n.osc.stop(); n.lfo.stop();
      n.osc.disconnect(); n.gain.disconnect();
      n.lfo.disconnect(); n.lfoG.disconnect();
    }
  };
}

// ---------------------------------------------------------------------------
// Hazard world position helper
// ---------------------------------------------------------------------------

function getHazardWorldPos(hazard: HazardState, planets: PlanetState[]): { x: number; y: number; z: number } {
  const planet = planets[hazard.planetIndex];
  const d = hazard.surfaceDir;
  return {
    x: planet.position.x + d.x * planet.radius,
    y: planet.position.y + d.y * planet.radius,
    z: planet.position.z + d.z * planet.radius,
  };
}

// Emitter config per hazard type
const EMITTER_CONFIG: Record<string, { ref: number; max: number; builder: typeof buildVolcanicEmitter }> = {
  volcanic: { ref: 5, max: 40, builder: buildVolcanicEmitter },
  geyser:   { ref: 4, max: 30, builder: buildGeyserEmitter },
  crystal:  { ref: 3, max: 25, builder: buildCrystalEmitter },
};

// ---------------------------------------------------------------------------
// Hazard Emitter Manager
// ---------------------------------------------------------------------------

export class HazardEmitterManager {
  private emitters: HazardEmitter[] = [];

  constructor(hazards: HazardState[], planets: PlanetState[]) {
    const audioCtx = getAudioContext();
    const bus = getAmbientBus();
    if (!audioCtx || !bus) return;

    for (const hazard of hazards) {
      if (!hazard.alive) continue;

      const config = EMITTER_CONFIG[hazard.hazardType];
      if (!config) continue;

      const pos = getHazardWorldPos(hazard, planets);
      const panner = createPanner(audioCtx, pos.x, pos.y, pos.z, config.ref, config.max);
      panner.connect(bus);

      const emitterGain = audioCtx.createGain();
      emitterGain.gain.value = 1.0;
      emitterGain.connect(panner);

      const cleanupFn = config.builder(audioCtx, panner, emitterGain);

      this.emitters.push({
        hazardId: hazard.id,
        panner,
        output: emitterGain,
        cleanup: cleanupFn,
      });
    }
  }

  /** Mute a hazard emitter (e.g. when destroyed). */
  muteHazard(hazardId: number): void {
    const emitter = this.emitters.find(e => e.hazardId === hazardId);
    if (emitter) {
      emitter.output.gain.value = 0;
    }
  }

  cleanup(): void {
    for (const emitter of this.emitters) {
      emitter.cleanup();
      emitter.output.disconnect();
      emitter.panner.disconnect();
    }
    this.emitters = [];
  }
}
